/**
 * OfstedUploadWidget — admin dashboard tile for dropping the monthly
 * DfE GIAS establishment CSV. The `ofsted_ingest_daemon` normally
 * runs on a 30-day cadence and needs the CSV at
 * `/app/backend/data/gias_establishments.csv`. This widget uploads
 * a new CSV via `POST /api/admin/ofsted/upload` and triggers a
 * one-shot daemon run so ratings appear immediately.
 *
 * Kept minimal — no drag-and-drop, no progress bar — since the
 * whole file is ~28 MB and takes a couple of seconds.
 */
import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

export default function OfstedUploadWidget() {
  const inputRef = useRef(null);
  const [state, setState] = useState({ status: "idle", err: null, result: null });

  const handleFile = async (file) => {
    if (!file) return;
    setState({ status: "uploading", err: null, result: null });
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/ofsted/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState({ status: "done", err: null, result: data });
    } catch (e) {
      const err = e?.response?.data?.detail || e?.message || "Upload failed";
      setState({ status: "error", err, result: null });
    }
  };

  return (
    <div
      data-testid="ofsted-upload-widget"
      className="rounded-md border border-stone-200 bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-stone-500" />
        <div className="label-uppercase font-semibold text-stone-700">
          Ofsted / GIAS CSV drop
        </div>
      </div>
      <p className="mt-2 text-[12px] text-stone-600 leading-snug">
        Monthly DfE establishment CSV.{" "}
        <a
          className="text-[#4C1D95] underline"
          href="https://get-information-schools.service.gov.uk/Downloads"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download from GIAS
        </a>
        {" "}(All establishments · CSV), then drop it here.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <input
          ref={inputRef}
          data-testid="ofsted-upload-input"
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          data-testid="ofsted-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={state.status === "uploading"}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#4C1D95] text-white px-3 py-1.5 text-[11px] font-underground-sc tracking-wider hover:bg-[#3a1476] disabled:opacity-50"
        >
          {state.status === "uploading" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Upload className="h-3 w-3" />
          )}
          {state.status === "uploading" ? "Uploading + ingesting…" : "Upload GIAS CSV"}
        </button>
      </div>

      {state.status === "done" && state.result && (
        <div
          data-testid="ofsted-upload-success"
          className="mt-3 rounded border border-emerald-200 bg-emerald-50/50 p-2 text-[12px] text-emerald-900"
        >
          <div className="font-semibold">Upload complete.</div>
          <div className="text-[11px] font-mono mt-1">
            {state.result.bytes_written?.toLocaleString()} bytes ·{" "}
            {state.result.rated_from_csv} rated from CSV ·{" "}
            {state.result.rated_total} total institutions rated
          </div>
        </div>
      )}

      {state.status === "error" && (
        <div
          data-testid="ofsted-upload-error"
          className="mt-3 rounded border border-rose-200 bg-rose-50/50 p-2 text-[12px] text-rose-900 flex items-start gap-1.5"
        >
          <AlertTriangle className="h-3 w-3 mt-[3px] shrink-0" />
          <span>{state.err}</span>
        </div>
      )}
    </div>
  );
}
